/**
 * UTM helper for print materials (leaflets, posters, business cards).
 *
 * Why: every QR on a printed asset should ride a UTM tag so the
 * existing signup-source pipeline (`lib/signupSource.js` →
 * `Register.jsx` → admin dashboard) can attribute scans to a specific
 * print campaign. Without UTM, every scan looks like "direct" and the
 * print push is invisible in analytics.
 *
 * Usage:
 *   import { withUtm, PRINT_UTM } from "../lib/utm";
 *   const QR_URL = withUtm(PUBLIC_URL, PRINT_UTM.leaflet);
 *
 * Idempotent — calling it twice on the same URL won't duplicate params;
 * existing utm_* params get replaced.
 */

/**
 * Append (or replace) UTM tracking params on a URL.
 *
 * @param {string} url    Base URL (e.g. "https://camp-compass.uk")
 * @param {object} tags   { source, medium, campaign, content?, term? }
 * @returns {string} URL with utm_* query params set.
 */
export function withUtm(url, tags = {}) {
  if (!url) return url;
  let u;
  try {
    u = new URL(url);
  } catch {
    // Fallback: bare string concatenation (rare — PUBLIC_URL is always absolute).
    const sep = url.includes("?") ? "&" : "?";
    const qs = [
      tags.source && `utm_source=${encodeURIComponent(tags.source)}`,
      tags.medium && `utm_medium=${encodeURIComponent(tags.medium)}`,
      tags.campaign && `utm_campaign=${encodeURIComponent(tags.campaign)}`,
      tags.content && `utm_content=${encodeURIComponent(tags.content)}`,
      tags.term && `utm_term=${encodeURIComponent(tags.term)}`,
    ].filter(Boolean).join("&");
    return qs ? `${url}${sep}${qs}` : url;
  }
  const set = (key, val) => {
    if (val) u.searchParams.set(key, val);
  };
  set("utm_source", tags.source);
  set("utm_medium", tags.medium);
  set("utm_campaign", tags.campaign);
  set("utm_content", tags.content);
  set("utm_term", tags.term);
  return u.toString();
}

/**
 * Canonical UTM presets for every print asset we ship. Keep medium = "print"
 * across the board so admin can filter "all print scans" with one query, then
 * drill down by source/campaign for per-asset breakdown.
 */
export const PRINT_UTM = {
  leaflet: {
    source: "leaflet",
    medium: "print",
    campaign: "campsite-reception",
  },
  poster: {
    source: "poster",
    medium: "print",
    campaign: "outdoor-show",
  },
  businessCard: {
    source: "businesscard",
    medium: "print",
    campaign: "word-of-mouth",
  },
};
